import React, { useEffect, useId, useRef, useState } from 'react';
import { CircleX, ClipboardList, Info, RotateCcw, Truck } from 'lucide-react';
import { validatePermission } from 'actions';
import { cooperplaceApi } from 'services/api';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import { formatDate } from 'v3/utils/controlTowerDate';
import { canReadCandidates, linkLabel, platesLabel } from '../Candidates/contract';
import { assetsOf, reasonsResponse, detailsResponse, mutationResponse } from './contract';
import {
  refusalActorKey,
  operationKey,
  readOperation,
  beginOperation,
  clearOperation,
} from './operation';
import useCurrentCompanyId from '../useCurrentCompanyId';
import { titleCase, upperCase } from '../text';

/**
 * variant="actions": Recusar ou, com recusa ativa, só o ícone Recusado (confirma e reverte).
 * variant="plate": ícone acima da placa quando há recusa nesta carga (ativa ou revertida).
 */
export default function RefusalActions({
  candidate,
  context,
  disabled,
  onOpenChange = () => {},
  onChanged,
  variant = 'actions',
}) {
  const reasonId = useId();
  const noteId = useId();
  const questionId = useId();
  const companyId = useCurrentCompanyId();
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;
  const [mode, setMode] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [details, setDetails] = useState([]);
  // Recusas ativas que o Sim reverte (uma pelo ⓘ da placa; todas do candidato pelo ícone Recusado).
  const [targets, setTargets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(false);
  const generation = useRef(0);
  const pending = useRef(false);
  const writing = useRef(false);
  const readable = companyId === context.companyId && canReadCandidates(validatePermission);
  const writable = readable && validatePermission('ATENDER_CARGA');
  const canRefuse =
    writable && candidate.profileEvaluation === 'COMPATIBLE' && !candidate.refusal.active;
  const hasHistory =
    candidate.refusal.active || candidate.plateHistory?.some((h) => h.refusals?.length);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  useEffect(() => {
    if (!readable) {
      generation.current += 1;
      pending.current = false;
      writing.current = false;
      setMode(null);
      setBusy(false);
      onOpenChange(false);
    }
  }, [readable, onOpenChange]);
  const close = () => {
    if (writing.current) return;
    generation.current += 1;
    pending.current = false;
    setMode(null);
    setBusy(false);
    setError(null);
    onOpenChange(false);
  };
  async function run(action) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const version = ++generation.current;
    try {
      const actor = refusalActorKey(context);
      const current = () =>
        generation.current === version &&
        companyIdRef.current === context.companyId &&
        refusalActorKey(context) === actor;
      await action(current);
    } catch (failure) {
      if (generation.current === version)
        setError(
          failure?.response?.data?.message ||
            failure.message ||
            'Não foi possível concluir a ação.',
        );
    } finally {
      if (generation.current === version) {
        pending.current = false;
        writing.current = false;
        setBusy(false);
      }
    }
  }
  const openRefusal = () => {
    if (!canRefuse || disabled) return;
    setMode('refuse');
    setReasons([]);
    setReason('');
    setNote('');
    setRetry(false);
    onOpenChange(true);
    run(async (current) => {
      const response = await cooperplaceApi.get('/control-tower/refusal-reasons');
      if (!current()) return;
      const list = reasonsResponse(response.data);
      const previous = readOperation(operationKey(context, candidate.vehicle));
      setReasons(list);
      setReason(previous?.reasonCode || '');
      setNote(previous?.note || '');
      setRetry(Boolean(previous));
    });
  };
  const openDetails = () => {
    if (!readable || disabled) return;
    setMode('details');
    setDetails([]);
    setTargets([]);
    setRetry(false);
    onOpenChange(true);
    run(async (current) => {
      const result = await Promise.all(
        assetsOf(candidate).map(async (asset) => {
          const { data } = await cooperplaceApi.get(
            `/control-tower/loads/${context.item.loadId}/plates/${asset.assetType}/${asset.id}/refusal`,
          );
          return detailsResponse(data, context, asset);
        }),
      );
      if (current()) setDetails(result);
    });
  };
  const reverse = (detail) => {
    if (!writable || busy || !detail.active) return;
    setTargets([detail]);
    setMode('reverse');
    setError(null);
    setRetry(false);
  };
  // Ícone Recusado: pede só a confirmação e reverte as recusas ativas do candidato, sem passar
  // pelos dados da recusa.
  const openReversal = () => {
    if (!writable || disabled) return;
    setMode('reverse');
    setTargets([]);
    setRetry(false);
    onOpenChange(true);
    run(async (current) => {
      const result = await Promise.all(
        assetsOf(candidate).map(async (asset) => {
          const { data } = await cooperplaceApi.get(
            `/control-tower/loads/${context.item.loadId}/plates/${asset.assetType}/${asset.id}/refusal`,
          );
          return detailsResponse(data, context, asset);
        }),
      );
      if (!current()) return;
      const active = result.filter((detail) => detail.active);
      if (!active.length) throw new Error('Nenhuma recusa ativa para reverter. Atualize a fila.');
      setTargets(active);
    });
  };
  const submit = () => {
    if (
      !writable ||
      disabled ||
      pending.current ||
      (mode === 'refuse' &&
        (!canRefuse || !reason || (!retry && !reasons.some((r) => r.code === reason))))
    )
      return;
    const reversal = mode === 'reverse';
    const items = reversal
      ? targets.filter((t) => t.active).map((t) => ({ asset: t.asset, refusalId: t.event.id }))
      : [{ asset: candidate.vehicle, refusalId: null }];
    if (!items.length || items.some((item) => !item.asset)) return;
    // 5703: observação opcional; sem texto, não vai no corpo.
    const refusalNote = note.trim();
    run(async (current) => {
      writing.current = true;
      setRetry(true);
      let replayed = false;
      for (const { asset, refusalId } of items) {
        const key = operationKey(context, asset, refusalId);
        const command = beginOperation(
          key,
          reversal
            ? { confirmed: true }
            : { reasonCode: reason, ...(refusalNote ? { note: refusalNote } : {}) },
        );
        const url = reversal
          ? `/control-tower/loads/${context.item.loadId}/plates/${asset.assetType}/${asset.id}/refusals/${refusalId}/reversal`
          : `/control-tower/loads/${context.item.loadId}/candidates/${candidate.vehicleId}/refusals`;
        let response;
        try {
          response = await cooperplaceApi.post(url, command);
        } catch (failure) {
          // Erros definitivos de validação/autorização não mantêm uma ação ambígua pendente.
          if ([401, 403, 404, 409, 422].includes(failure.response?.status)) {
            clearOperation(key);
            if (current()) setRetry(false);
          }
          throw failure;
        }
        const result = mutationResponse(response.data, context, asset, { ...command, refusalId });
        clearOperation(key);
        replayed = replayed || result.replayed;
      }
      if (!current()) return;
      const plates = items.map((item) => item.asset.plate).join(', ');
      const state = replayed ? 'Operação já registrada; consultando o estado atual.' : '';
      writing.current = false;
      close();
      if (reversal) onChanged(`Reversão confirmada para ${plates}.${state && ` ${state}`}`);
      else
        onChanged({
          confirmation: {
            title: 'Recusa confirmada',
            text: `Veículo ${plates} ficará na última posição da fila.`,
          },
          notice: state || null,
        });
    });
  };
  const loadLabel = `${context.item.type === 'PRE_LOAD' ? 'Pré-carga' : 'Carga'} ${context.item.loadId}`;
  // 5654: quadro do veículo como no modelo, com placas, carroceria e motorista.
  const bodies = [
    ...new Set(
      (candidate.wagons.length ? candidate.wagons : [candidate.vehicle])
        .map((asset) => asset.bodyTypeName)
        .filter(Boolean),
    ),
  ].join(' / ');
  const content =
    mode === 'refuse' ? (
      <>
        <div className="tc-refusal-vehicle">
          <span>
            {platesLabel(candidate)}
            {bodies && ` — ${bodies}`}
          </span>
          <span>{upperCase(candidate.driver?.name) || 'Sem motorista'}</span>
        </div>
        <label htmlFor={reasonId}>
          Motivo da recusa{' '}
          <span className="tc-refusal-required" aria-hidden="true">
            *
          </span>
        </label>
        <select
          id={reasonId}
          className="form-select"
          value={reason}
          disabled={busy || retry}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">Selecione o motivo</option>
          {reasons.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
          {retry && !reasons.some((r) => r.code === reason) && (
            <option value={reason}>{reason}</option>
          )}
        </select>
        {!busy && !error && !reasons.length && (
          <p>Nenhum motivo disponível. Atualize a consulta.</p>
        )}
        <label htmlFor={noteId} className="tc-refusal-note-label">
          Observação <span className="tc-refusal-optional">(opcional)</span>
        </label>
        <textarea
          id={noteId}
          className="form-control"
          rows={3}
          maxLength={500}
          placeholder="Informe algum detalhe adicional"
          value={note}
          disabled={busy || retry}
          onChange={(e) => setNote(e.target.value)}
        />
      </>
    ) : mode === 'reverse' ? (
      // 5657: confirmação curta do modelo; Sim reverte as recusas ativas, Não fecha sem alterar.
      <div className="tc-refusal-confirm">
        <RotateCcw size={36} aria-hidden="true" />
        <p id={questionId}>Deseja reverter a recusa?</p>
        <div>
          <Button disabled={busy || !writable || !targets.length} onClick={submit}>
            Sim
          </Button>
          <Button outlined disabled={writing.current} onClick={close}>
            Não
          </Button>
        </div>
      </div>
    ) : (
      // 5656: um quadro para o veículo e outro para cada carreta, como no modelo.
      <div className="tc-refusal-cards">
        {details.map((detail) => {
          const wagon = detail.asset.assetType === 'wagon';
          const driver = titleCase(candidate.driver?.name);
          const [day, time] = detail.event ? formatDate(detail.event.occurredAt).split(' ') : [];
          return (
            <section
              key={`${detail.asset.assetType}:${detail.asset.id}`}
              className="tc-refusal-card"
              aria-label={`${wagon ? 'Carreta' : 'Veículo'} ${detail.asset.plate}`}
            >
              <header>
                {!wagon && (
                  <span className="tc-refusal-icon">
                    <Truck size={18} aria-hidden="true" />
                  </span>
                )}
                <div>
                  <small>{wagon ? 'Carreta' : 'Veículo'}</small>
                  <strong>
                    {wagon
                      ? detail.asset.plate
                      : `Cavalo ${detail.asset.plate}${driver ? ` — ${driver}` : ''}`}
                  </strong>
                </div>
                {wagon && (
                  <ClipboardList size={18} aria-hidden="true" className="tc-refusal-card-mark" />
                )}
              </header>
              {!detail.event ? (
                <p className="tc-refusal-empty">
                  <Info size={16} aria-hidden="true" />
                  Nenhuma recusa registrada para {wagon ? 'esta carreta' : 'este veículo'} e placa.
                </p>
              ) : (
                <>
                  {!detail.active && <span className="tc-refusal-reverted">Recusa revertida</span>}
                  <dl>
                    <dt>Vínculo</dt>
                    <dd>{linkLabel(detail.event)}</dd>
                    <dt>Motivo</dt>
                    <dd>{detail.event.refusalReason.text}</dd>
                    <dt>Observação</dt>
                    {detail.event.refusalNote ? (
                      <dd className="tc-refusal-note">{detail.event.refusalNote}</dd>
                    ) : (
                      <dd className="tc-refusal-muted">Não informado</dd>
                    )}
                  </dl>
                  <footer>
                    Cancelamento em {day} às {time} por <strong>{detail.event.actor.name}</strong>
                  </footer>
                  {detail.active && (
                    <Button
                      size="sm"
                      variant="danger"
                      outlined
                      disabled={!writable || busy}
                      onClick={() => reverse(detail)}
                      aria-label={`Reverter recusa de ${detail.asset.plate}`}
                    >
                      Reverter recusa
                    </Button>
                  )}
                </>
              )}
            </section>
          );
        })}
      </div>
    );
  if (variant === 'plate' && !hasHistory) return null;
  const trigger =
    variant === 'plate' ? (
      <button
        type="button"
        className="tc-c-refusal-info"
        disabled={disabled || !readable}
        onClick={(event) => {
          event.stopPropagation();
          openDetails();
        }}
        aria-label={`Ver dados da recusa de ${platesLabel(candidate)}`}
        title="Ver dados da recusa"
      >
        <Info size={16} aria-hidden="true" />
      </button>
    ) : candidate.refusal.active ? (
      <button
        type="button"
        className="tc-c-refused-action"
        disabled={disabled || !readable}
        onClick={writable ? openReversal : openDetails}
        aria-label={`Recusado: reverter a recusa de ${platesLabel(candidate)}`}
        title={
          writable
            ? 'Recusado nesta carga. Reverter a recusa'
            : 'Recusado nesta carga. Ver dados da recusa'
        }
      >
        <RotateCcw size={16} aria-hidden="true" />
        <span>Recusado</span>
      </button>
    ) : (
      <button
        type="button"
        className="tc-c-refuse"
        disabled={disabled || !canRefuse}
        onClick={openRefusal}
        aria-label={`Recusar ${platesLabel(candidate)}`}
        title="Recusar veículo nesta carga"
      >
        <CircleX size={18} aria-hidden="true" />
      </button>
    );
  return (
    <>
      {trigger}
      <Modal
        show={Boolean(mode) && readable}
        className={
          mode === 'refuse'
            ? 'tc-refusal-modal'
            : mode === 'details'
              ? 'tc-refusal-details-modal'
              : 'tc-refusal-reverse-modal'
        }
        aria-labelledby={mode === 'reverse' ? questionId : undefined}
        handleClose={close}
        closeButton={!writing.current}
        backdrop={writing.current ? 'static' : true}
        keyboard={!writing.current}
        heading={
          mode === 'reverse' ? undefined : (
            <div className="tc-refusal-heading">
              <span className="tc-refusal-icon">
                <CircleX size={20} aria-hidden="true" />
              </span>
              <span>
                {mode === 'refuse' ? 'Recusar veículo' : 'Dados da recusa'}
                <small>{loadLabel}</small>
              </span>
            </div>
          )
        }
        body={
          <div className="tc-refusal-form">
            {content}
            {busy && <p role="status">{writing.current ? 'Registrando…' : 'Consultando…'}</p>}
            {retry && !busy && (
              <p>Confirme novamente para verificar a mesma operação após a falha de comunicação.</p>
            )}
            {error && (
              <div role="alert">
                <p>{error}</p>
                <Button
                  size="sm"
                  onClick={() => {
                    close();
                    onChanged();
                  }}
                >
                  Atualizar fila
                </Button>
              </div>
            )}
          </div>
        }
        footer={
          mode === 'reverse' ? undefined : (
            <>
              <Button variant="secondary" disabled={writing.current} onClick={close}>
                {mode === 'details' ? 'Fechar' : 'Cancelar'}
              </Button>
              {mode === 'refuse' && (
                <Button variant="danger" disabled={busy || !writable || !reason} onClick={submit}>
                  Confirmar recusa
                </Button>
              )}
            </>
          )
        }
      />
    </>
  );
}

/** Ícone acima da placa: dados da recusa desta carga, quando houver. */
export function RefusalPlateInfo(props) {
  return <RefusalActions {...props} variant="plate" />;
}
